import React, { useCallback } from 'react';

import { CheckSmall } from '@rover/icons';

import { Box, Flex } from '../../../core';
import { stopPropagation } from '../utils';

import { Props as CheckboxFieldOptionProps } from './CheckboxFieldOption';

export type Props = Pick<
  CheckboxFieldOptionProps,
  'fieldProps' | 'value' | 'id' | 'disabled' | 'required' | 'variant'
> & {
  describedBy?: string;
};

const CheckboxFieldOptionIcon = ({
  describedBy,
  disabled,
  fieldProps,
  id,
  required,
  value,
  variant,
}: Props): JSX.Element => {
  const handleOnChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) {
        // hilariously, this is the best way (a11y ux-wise) to get a
        // readonly checkbox that you can't change the value of
        e.target.checked = !e.target.checked;
      } else {
        // only call fieldProps.onChange if the input is not disabled
        fieldProps.onChange(e);
      }
    },
    [disabled, fieldProps]
  );
  return (
    <Flex flexShrink="0">
      {/* actual input is accessibly hidden */}
      <Box
        {...fieldProps}
        aria-describedby={describedBy}
        aria-disabled={disabled}
        aria-required={required}
        readOnly={disabled}
        as="input"
        type="checkbox"
        value={value}
        id={id}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0,
          zIndex: -1000,
        }}
        onClick={stopPropagation} // prevents calling the wrapper click handler when this element is clicked
        onChange={handleOnChange} // this overrides fieldProps.onChange
      />

      {/* fake Checkbox button comes second so we can use sibling (+) selectors to style based on input state */}
      {/* outer Box is everything but the check */}
      <Box
        width={variant === 'legalConsent' ? '4x' : '6x'}
        height={variant === 'legalConsent' ? '4x' : '6x'}
        mt={variant === 'legalConsent' ? '1x' : '0x'}
        border="2px solid"
        borderRadius="secondary"
        borderColor={disabled ? 'interactive_border.disabled_base' : 'border.primary'}
        background={disabled ? 'interactive.disabled_base' : 'primary'}
        sx={{
          /** checked input styles, the '&&' increases the priority of these rules */
          'input:checked + &&': {
            borderColor: disabled
              ? 'interactive_border.disabled_base'
              : 'interactive_border.primary_active',
            background: disabled ? 'tertiary' : 'contrast',

            // show check icon when input is checked
            '> svg': {
              display: 'block',
            },
          },
        }}
      >
        {/* inner Box is the check icon in the center */}
        <Box
          as={CheckSmall}
          display="none" // default to not displaying the check, this is overridden by styles in the previous Box
          height="12px"
          width="12px"
          m={variant === 'legalConsent' ? '0x' : '1x'}
          // this "zooms in" on the check icon and removes the "padding" that is present in the svg
          viewBox="8 8 16 16"
          sx={{
            fill: 'neutral.white',
          }}
        />
      </Box>
    </Flex>
  );
};

export default CheckboxFieldOptionIcon;
